import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Upload,
  BookOpen,
  MessageCircle,
  Brain,
  StickyNote,
  Volume2,
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { Button } from './ui/button';
import { useI18n, type TranslateFn, type TranslateListFn } from '../../i18n';

const ONBOARDING_STORAGE_KEY = 'sourcity_onboarding_completed';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface OnboardingStep {
  key: string;
  icon: React.ElementType;
  iconColor: string;
  illustration: React.ReactNode;
}

/** Mockup markup only; every visible string is resolved from the locale files. */
function buildSteps(t: TranslateFn, tList: TranslateListFn): OnboardingStep[] {
  return [
  {
    key: 'welcome',
    icon: Sparkles,
    iconColor: 'from-[#7367FF] to-[#2DD2FE]',
    illustration: (
      <div className="relative w-full max-w-[320px] mx-auto">
        <div className="bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 rounded-2xl p-8 border border-[#7367FF]/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="h-3 w-24 bg-[#7367FF]/20 rounded-full" />
              <div className="h-2 w-16 bg-[#7367FF]/10 rounded-full mt-1.5" />
            </div>
          </div>
          <div className="space-y-3">
            {tList('onboarding.steps.welcome.labels').map((label, i) => (
              <div key={label} className="flex items-center gap-3 bg-white/60 rounded-xl p-3 backdrop-blur-sm">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${i === 0 ? 'from-[#7367FF] to-[#2DD2FE]' : i === 1 ? 'from-[#2DD2FE] to-[#3CFF9B]' : 'from-[#3CFF9B] to-[#7367FF]'} flex items-center justify-center`}>
                  <span className="text-white text-xs font-bold">{i + 1}</span>
                </div>
                <span className="text-sm font-medium text-[#434343]">{label}</span>
                <CheckCircle2 className="h-4 w-4 text-[#3CFF9B] ms-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'upload',
    icon: Upload,
    iconColor: 'from-[#7367FF] to-[#2DD2FE]',
    illustration: (
      <div className="relative w-full max-w-[320px] mx-auto">
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-200 shadow-lg">
          {/* Mock sidebar */}
          <div className="flex gap-2 mb-4">
            {tList('onboarding.steps.upload.tabs').map((tab, i) => (
              <div key={tab} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${i === 0 ? 'bg-[#7367FF]/10 text-[#7367FF]' : 'text-[#434343]/40'}`}>
                {tab}
              </div>
            ))}
          </div>
          {/* Mock upload zone */}
          <div className="border-2 border-dashed border-[#7367FF]/30 rounded-xl p-6 text-center bg-[#7367FF]/5 mb-4">
            <Upload className="h-8 w-8 text-[#7367FF] mx-auto mb-2" />
            <p className="text-xs text-[#7367FF] font-medium">{t('onboarding.steps.upload.dropHere')}</p>
          </div>
          {/* Mock file items */}
          {tList('onboarding.steps.upload.mockFiles').map((name, i) => (
            <div key={name} className={`flex items-center gap-2 p-2 rounded-lg mb-1.5 ${i === 0 ? 'bg-[#7367FF]/10' : 'bg-gray-50'}`}>
              <div className={`w-6 h-6 rounded ${i === 0 ? 'bg-[#7367FF]' : 'bg-gray-300'}`} />
              <span className="text-xs text-[#434343] truncate">{name}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    key: 'read',
    icon: Volume2,
    iconColor: 'from-[#2DD2FE] to-[#3CFF9B]',
    illustration: (
      <div className="relative w-full max-w-[320px] mx-auto">
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-gray-200 shadow-lg">
          {/* Mock toolbar */}
          <div className="flex items-center justify-between mb-3 px-2 py-1.5 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-1">
              <div className="w-5 h-5 rounded bg-gray-200" />
              <span className="text-[10px] text-[#434343]">{t('onboarding.steps.read.mockPage')}</span>
              <div className="w-5 h-5 rounded bg-gray-200" />
            </div>
            <div className="flex items-center gap-1">
              <div className="px-2 py-0.5 rounded bg-green-100 text-[10px] text-green-600">{t('onboarding.steps.read.audioReady')}</div>
              <div className="w-5 h-5 rounded bg-[#7367FF]/20" />
            </div>
          </div>
          {/* Mock text with selection */}
          <div className="space-y-2 px-2">
            <div className="h-2.5 bg-gray-100 rounded-full w-full" />
            <div className="h-2.5 bg-gray-100 rounded-full w-[90%]" />
            <div className="h-2.5 bg-[#7367FF]/20 rounded-full w-[85%] relative">
              {/* Selection popup */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1 bg-[#434343] rounded-lg p-1 shadow-lg">
                <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                  <Volume2 className="h-3 w-3 text-white" />
                </div>
                <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                  <Brain className="h-3 w-3 text-white" />
                </div>
                <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                  <StickyNote className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>
            <div className="h-2.5 bg-[#7367FF]/20 rounded-full w-[60%]" />
            <div className="h-2.5 bg-gray-100 rounded-full w-full" />
            <div className="h-2.5 bg-gray-100 rounded-full w-[75%]" />
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'ask',
    icon: MessageCircle,
    iconColor: 'from-[#7367FF] to-[#2DD2FE]',
    illustration: (
      <div className="relative w-full max-w-[320px] mx-auto">
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-gray-200 shadow-lg">
          {/* Mock chat header */}
          <div className="flex items-center gap-2 pb-3 border-b border-gray-100 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-[#434343]">{t('chat.title')}</p>
              <p className="text-[10px] text-[#434343]/50">{t('onboarding.steps.ask.mockDocument')}</p>
            </div>
          </div>
          {/* Mock messages */}
          <div className="space-y-3 mb-3">
            <div className="flex justify-end">
              <div className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white rounded-2xl px-3 py-2 text-xs max-w-[200px]">
                {t('onboarding.steps.ask.mockQuestion')}
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center flex-shrink-0">
                <span className="text-[8px] text-white font-bold">AI</span>
              </div>
              <div className="bg-gray-50 rounded-2xl px-3 py-2 text-xs text-[#434343] max-w-[200px]">
                {t('onboarding.steps.ask.mockAnswer')}
              </div>
            </div>
          </div>
          {/* Mock input */}
          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex-1 h-4 bg-gray-100 rounded" />
            <div className="w-7 h-7 rounded-lg bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] flex items-center justify-center">
              <ChevronRight className="h-3 w-3 text-white" />
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'quiz',
    icon: Brain,
    iconColor: 'from-[#3CFF9B] to-[#7367FF]',
    illustration: (
      <div className="relative w-full max-w-[320px] mx-auto">
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 border border-gray-200 shadow-lg">
          {/* Mock quiz header */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-[#434343]/60">{t('onboarding.steps.quiz.mockProgress')}</span>
            <div className="flex gap-0.5">
              {[1,2,3,4,5,6,7,8,9,10].map(i => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i <= 3 ? 'bg-[#7367FF]' : 'bg-gray-200'} ${i === 3 ? 'w-4' : ''}`} />
              ))}
            </div>
          </div>
          {/* Mock question */}
          <div className="mb-3">
            <p className="text-[10px] text-[#7367FF] uppercase font-medium mb-1">{t('onboarding.steps.quiz.mockLabel')}</p>
            <p className="text-xs text-[#434343] font-medium">{t('onboarding.steps.quiz.mockQuestion')}</p>
          </div>
          {/* Mock options */}
          <div className="space-y-1.5">
            {tList('onboarding.steps.quiz.mockOptions').map((opt, i) => (
              <div key={opt} className={`flex items-center gap-2 p-2 rounded-lg text-xs border ${
                i === 1 ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-100 bg-gray-50 text-[#434343]'
              }`}>
                <div className={`w-3.5 h-3.5 rounded-full border-2 ${i === 1 ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                  {i === 1 && <CheckCircle2 className="h-3.5 w-3.5 text-white -m-[1px]" />}
                </div>
                {opt}
              </div>
            ))}
          </div>
          {/* Explanation */}
          <div className="mt-2 p-2 rounded-lg bg-[#7367FF]/5 border border-[#7367FF]/20">
            <p className="text-[10px] text-[#434343]/70"><span className="text-[#7367FF] font-medium">{t('onboarding.steps.quiz.mockCorrect')}</span>{t('onboarding.steps.quiz.mockExplanation')}</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'done',
    icon: StickyNote,
    iconColor: 'from-[#2DD2FE] to-[#3CFF9B]',
    illustration: (
      <div className="relative w-full max-w-[320px] mx-auto">
        <div className="bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 rounded-2xl p-8 border border-[#7367FF]/20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center shadow-lg shadow-[#7367FF]/30">
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <h4 className="text-base font-display text-[#434343] mb-2">{t('onboarding.steps.done.readyTitle')}</h4>
          <p className="text-xs text-[#434343]/60 mb-4">{t('onboarding.steps.done.readyBody')}</p>
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#7367FF]/20 flex items-center justify-center">
              <Upload className="h-4 w-4 text-[#7367FF]" />
            </div>
            <div className="w-8 h-8 rounded-lg bg-[#2DD2FE]/20 flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-[#2DD2FE]" />
            </div>
            <div className="w-8 h-8 rounded-lg bg-[#7367FF]/20 flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-[#7367FF]" />
            </div>
            <div className="w-8 h-8 rounded-lg bg-[#3CFF9B]/20 flex items-center justify-center">
              <Brain className="h-4 w-4 text-[#3CFF9B]" />
            </div>
          </div>
        </div>
      </div>
    ),
  },
  ];
}

export function OnboardingModal({ isOpen, onClose }: OnboardingModalProps) {
  const { t, tList } = useI18n();
  const steps = useMemo(() => buildSteps(t, tList), [t, tList]);
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    onClose();
  };

  const step = steps[currentStep];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleSkip}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Close / Skip */}
            <button
              onClick={handleSkip}
              className="absolute top-4 end-4 z-20 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="h-4 w-4 text-[#434343]/60" />
            </button>

            {/* Content */}
            <div className="p-8 sm:p-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    {/* Left: Text */}
                    <div className="order-2 md:order-1">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.iconColor} flex items-center justify-center mb-4 shadow-lg`}>
                        <step.icon className="h-6 w-6 text-white" />
                      </div>

                      <h2 className="text-2xl font-display text-[#434343] mb-3">
                        {t(`onboarding.steps.${step.key}.title`)}
                      </h2>

                      <p className="text-sm text-[#434343]/70 mb-6 leading-relaxed">
                        {t(`onboarding.steps.${step.key}.description`)}
                      </p>

                      <div className="space-y-3">
                        {tList(`onboarding.steps.${step.key}.features`).map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2.5">
                            <CheckCircle2 className="h-4 w-4 text-[#3CFF9B] flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-[#434343]/80">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right: Illustration */}
                    <div className="order-1 md:order-2">
                      {step.illustration}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="px-8 sm:px-10 pb-8 sm:pb-10">
              <div className="flex items-center justify-between">
                {/* Progress Dots */}
                <div className="flex gap-1.5">
                  {steps.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentStep(idx)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        idx === currentStep
                          ? 'w-8 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE]'
                          : idx < currentStep
                          ? 'w-2 bg-[#7367FF]/40'
                          : 'w-2 bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center gap-3">
                  {currentStep > 0 && (
                    <Button
                      variant="ghost"
                      onClick={handlePrev}
                      className="text-[#434343] hover:bg-gray-100"
                    >
                      <ChevronLeft className="h-4 w-4 me-1 rtl-flip" />
                      {t('onboarding.back')}
                    </Button>
                  )}
                  {currentStep === 0 && (
                    <Button
                      variant="ghost"
                      onClick={handleSkip}
                      className="text-[#434343]/60 hover:bg-gray-100"
                    >
                      {t('onboarding.skip')}
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    className="bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] hover:opacity-90 text-white shadow-lg shadow-[#7367FF]/30 px-6"
                  >
                    {currentStep === steps.length - 1 ? (
                      t('onboarding.getStarted')
                    ) : (
                      <>
                        {t('onboarding.next')}
                        <ChevronRight className="h-4 w-4 ms-1 rtl-flip" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/**
 * Hook to manage onboarding state
 */
export function useOnboarding() {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forceOnboarding = params.get('onboarding') === '1' || params.get('onboarding') === 'true';
    const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (forceOnboarding || !completed) {
      // Small delay so the app loads first
      const timer = setTimeout(() => setShowOnboarding(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
  };

  return { showOnboarding, closeOnboarding };
}
